import {HttpError} from '../../src/services/http/httpError';
import {ThunkAsyncAction} from '../../src/store/appThunk';
import {getUserId} from '../../src/store/data/teammates/teammatesSelectors';
import {
  finishOptimisticUpdateAction,
  startOptimisticUpdateAction,
} from '../../src/store/optimisticUpdates/optimisticUpdatesActions';
import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {logger} from '../../srcCommon/services/logger/logger';
import {OfflineError} from '../../srcCommon/services/offline/offlineError';
import {PersistedStorageUnsupportedError} from '../../srcCommon/services/persistentState/persistedState';
import {AppTask, saveAppTaskAsync, tryLoadAppTasksAsync} from './appTask';
import {taskStore} from './taskStore';

const offlineContentLogger = logger.getLogger('offlineContent');

/*
 * Action creators.
 */

/** Schedule an offline task to be performed the next time we are online. */
export function scheduleOfflineTaskAsyncAction(
  task: AppTask,
  throwIfUnsupported?: unknown,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);

    // Show and keep the optimistic UI update.
    dispatch(startOptimisticUpdateAction(task.id, task.optimisticUpdater));

    try {
      // Save the task to persisted storage.
      await saveAppTaskAsync(userId, task);

      offlineContentLogger.debug('Saved offline task', {
        task_id: task.id,
        task_name: task.name,
      });
    } catch (error) {
      // Remove the optimistic UI update if we failed to persist the task.
      dispatch(finishOptimisticUpdateAction(task.id));

      if (error instanceof PersistedStorageUnsupportedError && throwIfUnsupported) {
        throw throwIfUnsupported;
      }

      throw error;
    }
  };
}

/** Load previously scheduled offline tasks from persisted storage. */
export function loadOfflineTasksAsyncAction(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);

    // Load the tasks from persisted storage.
    const tasks = await tryLoadAppTasksAsync(userId);

    // Revive the optimistic UI updates.
    tasks.forEach((task) => {
      try {
        dispatch(startOptimisticUpdateAction(task.id, task.optimisticUpdater));
      } catch (error) {
        reportToBugsnag(error);
      }
    });
  };
}

/** Flush all offline tasks. */
export function flushAllOfflineTasksAsyncAction(): ThunkAsyncAction {
  // prettier-ignore
  return async (dispatch, getState) => {
    const state = getState();
    const userId = getUserId(state);

    // Load the tasks from persisted storage.
    const tasks = await tryLoadAppTasksAsync(userId);

    offlineContentLogger.debug('Flushing offline tasks', {
      size: tasks.length
    });

    for (const task of tasks)
      // eslint-disable-next-line no-await-in-loop
      {await dispatch(flushOfflineTaskAsyncAction(task));}
  };
}

/** Flush a single offline task. */
export function flushOfflineTaskAsyncAction(task: AppTask): ThunkAsyncAction {
  return async (dispatch, getState) => {
    try {
      // The task performer can finish the optimistic action explicitly or implicitly.
      const implicitResultAction = await task.performer(dispatch, getState, (explicitResultAction) => {
        dispatch(finishOptimisticUpdateAction(task.id, explicitResultAction));
      });
      dispatch(finishOptimisticUpdateAction(task.id, implicitResultAction || undefined));

      offlineContentLogger.debug('Flushed offline task', {
        task_id: task.id,
        task_name: task.name,
      });

      await taskStore.tryRemoveTaskAsync(task.id);
    } catch (error) {
      if (error instanceof OfflineError) {
        offlineContentLogger.debug('Failed to flush offline task, we are offline', {
          task_id: task.id,
          task_name: task.name,
        });
        throw error;
      }

      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }

      dispatch(finishOptimisticUpdateAction(task.id));

      await taskStore.tryRemoveTaskAsync(task.id);
    }
  };
}

/** Perform a single offline task. For testing purposes. */
export function performOfflineTaskAsyncAction(task: AppTask): ThunkAsyncAction {
  return async (dispatch) => {
    dispatch(startOptimisticUpdateAction(task.id, task.optimisticUpdater));
    await dispatch(flushOfflineTaskAsyncAction(task));
  };
}

export function cancelOfflineTaskAsyncAction(taskId: string): ThunkAsyncAction {
  return async (dispatch) => {
    await taskStore.removeTaskAsync(taskId);
    dispatch(finishOptimisticUpdateAction(taskId));
  };
}
